import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Toast from '../components/Toast';
import { MiniPill } from '../components/ui';

function IconTile({ tone = 'brand', children }) {
  const tones = {
    brand: 'bg-brand-100 text-brand-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    sky: 'bg-sky-100 text-sky-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
  };
  return (
    <span className={`grid place-items-center shrink-0 w-12 h-12 rounded-lg ${tones[tone] || tones.brand}`}>
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    connected: { tone: 'emerald', label: 'Connected' },
    manual: { tone: 'amber', label: 'Manual entry' },
    available: { tone: 'gray', label: 'Available' },
  };
  const s = map[status] || map.available;
  return <MiniPill tone={s.tone}>{s.label}</MiniPill>;
}

const ICONS = {
  drive: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M7.71 3.5L1.15 15l3.43 5.96h6.86l-3.43-5.96L7.71 3.5zm1.14 0l6.56 11.5H22l-6.56-11.5H8.85zm6.28 12.5l-3.43 5.96h13.15L21.42 16H15.13z" /></svg>
  ),
  slack: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14h6M14 14h6M14 4v6M14 14v6M4 10h6M10 10v10M4 14h6M14 4h6" /></svg>
  ),
};

function IntegrationCard({ icon, name, tagline, status, footer }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-brand-300 hover:shadow-sm transition p-5">
      <div className="flex items-start gap-4">
        <IconTile>{ICONS[icon]}</IconTile>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-heading text-[16px] text-ink dark:text-gray-100">{name}</h4>
            <StatusPill status={status} />
          </div>
          <p className="text-[13px] text-gray-500 mt-1">{tagline}</p>
        </div>
      </div>
      <div className="border-t border-gray-100 dark:border-gray-700 mt-4 pt-3 flex items-center justify-end gap-2">{footer}</div>
    </div>
  );
}

export default function IntegrationsPage() {
  const [toast, setToast] = useState(null);
  const [driveStatus, setDriveStatus] = useState({ configured: false, connected: false, loading: true });
  const [driveConnecting, setDriveConnecting] = useState(false);

  useEffect(() => {
    api.get('/google-drive/status')
      .then(res => setDriveStatus({ ...res.data, loading: false }))
      .catch(() => setDriveStatus({ configured: false, connected: false, loading: false }));

    const params = new URLSearchParams(window.location.search);
    const driveParam = params.get('drive');
    if (driveParam === 'connected') {
      setToast({ message: 'Google Drive connected successfully!', type: 'success' });
      setDriveStatus({ configured: true, connected: true, loading: false });
      window.history.replaceState({}, '', '/settings/integrations');
    } else if (driveParam === 'error') {
      setToast({ message: 'Google Drive connection failed: ' + (params.get('message') || 'Unknown error'), type: 'error' });
      window.history.replaceState({}, '', '/settings/integrations');
    }
  }, []);

  const handleConnectDrive = async () => {
    setDriveConnecting(true);
    try {
      const res = await api.get('/google-drive/authorize');
      window.location.href = res.data.url;
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to start Google Drive connection', type: 'error' });
      setDriveConnecting(false);
    }
  };

  const handleDisconnectDrive = async () => {
    try {
      await api.post('/google-drive/disconnect');
      setDriveStatus({ configured: true, connected: false, loading: false });
      setToast({ message: 'Google Drive disconnected', type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to disconnect Google Drive', type: 'error' });
    }
  };

  const stubConnect = (name) => () => setToast({ message: `${name} integration is coming soon.`, type: 'success' });

  // Drive footer changes by connection state
  let driveStatusKey, driveFooter;
  if (driveStatus.loading) {
    driveStatusKey = 'available';
    driveFooter = <span className="text-[12.5px] text-gray-400">Checking…</span>;
  } else if (driveStatus.connected) {
    driveStatusKey = 'connected';
    driveFooter = (
      <>
        <a href="https://drive.google.com" target="_blank" rel="noreferrer" className="text-[13px] font-medium text-gray-600 hover:text-brand-700">Configure</a>
        <button onClick={handleDisconnectDrive} className="text-[13px] font-medium text-rose-600 hover:text-rose-700">Disconnect</button>
      </>
    );
  } else {
    driveStatusKey = 'available';
    driveFooter = (
      <button onClick={handleConnectDrive} disabled={driveConnecting}
        className="px-4 py-1.5 rounded-md text-[13px] font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
        {driveConnecting ? 'Connecting…' : 'Connect'}
      </button>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <header className="mb-6">
        <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Integrations</h1>
        <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2 max-w-[640px]">
          Connect external services so document uploads, payroll exports, and notifications happen automatically.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <IntegrationCard
          icon="drive" name="Google Drive"
          tagline="Store client document uploads in your team's Shared Drive, auto-organised by company and period."
          status={driveStatusKey}
          footer={driveFooter}
        />
        <IntegrationCard
          icon="slack" name="Slack"
          tagline="Get a ping in your team channel when a client submits or a deadline slips. Admin-only setup; on the roadmap."
          status="available"
          footer={
            <button onClick={stubConnect('Slack')} className="px-4 py-1.5 rounded-md text-[13px] font-semibold bg-brand-600 text-white hover:bg-brand-700">
              Connect
            </button>
          }
        />
      </div>

      {!driveStatus.loading && driveStatus.configured && !driveStatus.connected && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-200 dark:ring-amber-800 rounded-lg text-[12.5px] text-amber-700 dark:text-amber-400">
          Google Drive is configured but not connected. Document uploads will be stored on the server until you reconnect.
        </div>
      )}
    </div>
  );
}
